import type { ConsentUiText } from './consent-i18n';
import type { ProviderModel } from './consent-providers';

/**
 * Markup and styles for the consent banner and preference dialog. Everything here is a pure
 * string builder: the controller in `consent.ts` owns state, events and persistence. Every value
 * that comes from a policy is escaped; nothing from a policy is ever inserted as HTML.
 */

export interface ConsentViewCategory {
  key: string;
  label: string;
  description: string;
  required: boolean;
}

export interface ConsentViewService {
  label: string;
  description: string;
}

export interface ConsentBannerViewInput {
  idPrefix: string;
  title: string;
  body: string;
  privacyPolicyUrl: string | null;
  text: ConsentUiText;
}

export const LEGAL_DISCLOSURE_KEY = 'legal';

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Policy text in its own bidi isolate: a Latin provider name inside Arabic copy (or the
 * reverse) keeps its own direction without flipping the row's alignment.
 */
function isolated(value: string): string {
  return `<bdi>${escapeHtml(value)}</bdi>`;
}

// Inline, decorative icons: no icon library and no remote asset just to draw a chevron.
const ICON_ATTRS =
  'class="perspectify-consent-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
const CLOSE_ICON = `<svg ${ICON_ATTRS}><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const CHEVRON_ICON = `<svg ${ICON_ATTRS} data-icon="chevron"><path d="m6 9 6 6 6-6"/></svg>`;
const LOCK_ICON = `<svg ${ICON_ATTRS}><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>`;
const EXTERNAL_ICON = `<svg ${ICON_ATTRS} data-icon="external"><path d="M7 17 17 7M8 7h9v9"/></svg>`;

function externalLink(url: string, label: string, accessibleName: string, className: string): string {
  // Escaping an attribute does not make a javascript: or data: URL safe.
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return '';
  }
  if (target.protocol !== 'https:') return '';
  return `<a class="${className}" href="${escapeHtml(target.href)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(
    accessibleName
  )}">${escapeHtml(label)}${EXTERNAL_ICON}</a>`;
}

const SHIELD_ICON = `<svg ${ICON_ATTRS} data-icon="shield"><path d="M12 3 5 6v5c0 4.4 3 8.2 7 9.5 4-1.3 7-5.1 7-9.5V6l-7-3Z"/><path d="m9 12 2 2 4-4"/></svg>`;

/**
 * The floating launcher: an icon whose accessible name is the configured label. The visible
 * tooltip repeats that label for sighted pointer and keyboard users, so it is hidden from
 * assistive technology to avoid announcing it twice.
 */
export function renderLauncherMarkup(label: string): string {
  return `${SHIELD_ICON}<span class="perspectify-consent-tooltip" aria-hidden="true">${isolated(label)}</span>`;
}

export function renderBannerMarkup(input: ConsentBannerViewInput): string {
  const { text } = input;
  return `
    <div class="perspectify-consent-banner-inner">
      <div class="perspectify-consent-banner-copy">
        <h2 class="perspectify-consent-banner-title" id="${input.idPrefix}-banner-title">${isolated(input.title)}</h2>
        <p class="perspectify-consent-copy">${isolated(input.body)}</p>
        ${
          input.privacyPolicyUrl
            ? `<p class="perspectify-consent-link-row">${externalLink(
                input.privacyPolicyUrl,
                text.privacyPolicy,
                text.privacyPolicyNewTab,
                'perspectify-consent-link'
              )}</p>`
            : ''
        }
      </div>
      <div class="perspectify-consent-banner-actions">
        <button type="button" data-action="reject" class="perspectify-consent-button perspectify-consent-button--filled">${escapeHtml(text.rejectAll)}</button>
        <button type="button" data-action="accept" class="perspectify-consent-button perspectify-consent-button--filled">${escapeHtml(text.acceptAll)}</button>
        <button type="button" data-action="preferences" class="perspectify-consent-button perspectify-consent-button--outline perspectify-consent-banner-manage">${escapeHtml(text.manageChoices)}</button>
      </div>
    </div>
  `;
}

const ICON_FUNCTIONAL = `<svg ${ICON_ATTRS} data-icon="functional"><path d="M4 7h9M17 7h3M4 17h3M11 17h9"/><circle cx="15" cy="7" r="2"/><circle cx="9" cy="17" r="2"/></svg>`;
const ICON_ANALYTICS = `<svg ${ICON_ATTRS} data-icon="analytics"><path d="M3 20h18M6 20v-8M12 20V5M18 20v-5"/></svg>`;
const ICON_MARKETING = `<svg ${ICON_ATTRS} data-icon="marketing"><path d="M4 10v4h3l6 4V6l-6 4H4Z"/><path d="M16.5 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11"/></svg>`;
const ICON_SUPPORT = `<svg ${ICON_ATTRS} data-icon="support"><path d="M20 11.5a7.5 7.5 0 0 1-10.9 6.7L4 19.5l1.3-4.6A7.5 7.5 0 1 1 20 11.5Z"/><path d="M9 11.5h.01M12.5 11.5h.01M16 11.5h.01"/></svg>`;
const SEARCH_ICON = `<svg ${ICON_ATTRS}><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2"/></svg>`;
const ARROW_PREVIOUS_ICON = `<svg ${ICON_ATTRS} data-icon="direction"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>`;
const ARROW_NEXT_ICON = `<svg ${ICON_ATTRS} data-icon="direction"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;
const CHEVRON_NEXT_ICON = `<svg ${ICON_ATTRS} data-icon="details-chevron"><path d="m9 6 6 6-6 6"/></svg>`;

const CATEGORY_ICONS: Record<string, string> = {
  necessary: LOCK_ICON,
  functional: ICON_FUNCTIONAL,
  analytics: ICON_ANALYTICS,
  marketing: ICON_MARKETING,
  support: ICON_SUPPORT,
};

export const ABOUT_DISCLOSURE_KEY = 'about';

export interface ConsentSheetCategory {
  key: string;
  label: string;
  description: string;
  required: boolean;
  /** The pending choice: the saved one, or a draft the visitor has not saved yet. */
  checked: boolean;
  services: ConsentViewService[];
}

export interface ConsentPreferencesViewInput {
  idPrefix: string;
  title: string;
  privacyPolicyUrl: string | null;
  legalDetails: string[];
  categories: ConsentSheetCategory[];
  providerTotal: number;
  /** Disclosures that start open: `about` and/or `legal`. */
  expanded: ReadonlySet<string>;
  text: ConsentUiText;
}

export interface ConsentProviderEntriesInput {
  idPrefix: string;
  items: ProviderModel[];
  openId: string | null;
  /** `none`: the policy lists no providers; `no-match`: the search found none. */
  emptyKind: 'none' | 'no-match' | null;
  text: ConsentUiText;
}

export function preferencesIds(idPrefix: string) {
  return {
    title: `${idPrefix}-title`,
    intro: `${idPrefix}-intro`,
    about: `${idPrefix}-about`,
    legal: `${idPrefix}-legal`,
    providersTitle: `${idPrefix}-providers-title`,
    list: `${idPrefix}-providers`,
    status: `${idPrefix}-provider-status`,
    description: (key: string) => `${idPrefix}-${key}-description`,
    detail: (providerId: string) => `${idPrefix}-provider-${providerId}`,
    summary: (providerId: string) => `${idPrefix}-provider-${providerId}-summary`,
  };
}

function renderCategoryOption(category: ConsentSheetCategory, input: ConsentPreferencesViewInput): string {
  const ids = preferencesIds(input.idPrefix);
  const hasDescription = category.description.trim() !== '' || category.services.length > 0;
  return `
    <label class="perspectify-consent-option" data-category="${escapeHtml(category.key)}">
      ${CATEGORY_ICONS[category.key] ?? ''}
      <span class="perspectify-consent-option-label">${isolated(category.label)}</span>
      <input type="checkbox" class="perspectify-consent-toggle" data-key="${escapeHtml(category.key)}"${
        category.checked ? ' checked' : ''
      }${hasDescription ? ` aria-describedby="${ids.description(category.key)}"` : ''}>
    </label>
  `;
}

function renderCategoryExplanation(category: ConsentSheetCategory, input: ConsentPreferencesViewInput): string {
  const { text } = input;
  const ids = preferencesIds(input.idPrefix);
  const description = category.description.trim();
  return `
    <div class="perspectify-consent-about-item" data-category="${escapeHtml(category.key)}">
      <dt class="perspectify-consent-about-term">${CATEGORY_ICONS[category.key] ?? ''}<span>${isolated(category.label)}</span>${
        category.required ? `<span class="perspectify-consent-about-status">${escapeHtml(text.alwaysOn)}</span>` : ''
      }</dt>
      <dd class="perspectify-consent-about-detail" id="${ids.description(category.key)}">
        ${description ? `<p class="perspectify-consent-about-copy">${isolated(description)}</p>` : ''}
        ${
          category.services.length > 0
            ? `<ul class="perspectify-consent-service-list" aria-label="${escapeHtml(text.servicesInCategory)}">${category.services
                .map(
                  (service) =>
                    `<li class="perspectify-consent-service"><span class="perspectify-consent-service-name">${isolated(service.label)}</span>${
                      service.description.trim() ? `<span class="perspectify-consent-service-copy">${isolated(service.description)}</span>` : ''
                    }</li>`
                )
                .join('')}</ul>`
            : ''
        }
      </dd>
    </div>
  `;
}

export function renderPreferencesMarkup(input: ConsentPreferencesViewInput): string {
  const { text } = input;
  const ids = preferencesIds(input.idPrefix);
  const optional = input.categories.filter((category) => !category.required);
  const required = input.categories.filter((category) => category.required);
  const hasLegal = input.legalDetails.length > 0;
  const aboutOpen = input.expanded.has(ABOUT_DISCLOSURE_KEY);
  const legalOpen = hasLegal && input.expanded.has(LEGAL_DISCLOSURE_KEY);
  const hasProviders = input.providerTotal > 0;
  const meta = [
    input.privacyPolicyUrl
      ? externalLink(input.privacyPolicyUrl, text.privacyPolicy, text.privacyPolicyNewTab, 'perspectify-consent-link perspectify-consent-meta-link')
      : '',
    hasLegal
      ? `<button type="button" class="perspectify-consent-disclosure perspectify-consent-legal-toggle" data-disclosure="${LEGAL_DISCLOSURE_KEY}" aria-expanded="${legalOpen}" aria-controls="${ids.legal}"><span>${escapeHtml(
          text.controllerDetails
        )}</span>${CHEVRON_ICON}</button>`
      : '',
  ].filter(Boolean);

  return `
    <div class="perspectify-consent-modal perspectify-consent-sheet" role="dialog" aria-modal="true" aria-labelledby="${ids.title}" aria-describedby="${ids.intro}">
      <div class="perspectify-consent-header">
        <p class="perspectify-consent-brand"><span class="perspectify-consent-brand-mark">${SHIELD_ICON}</span><span>${escapeHtml(text.privacyEyebrow)}</span></p>
        <button type="button" data-action="close" class="perspectify-consent-icon-button perspectify-consent-close" aria-label="${escapeHtml(
          text.close
        )}" title="${escapeHtml(text.close)}">${CLOSE_ICON}</button>
        <h2 class="perspectify-consent-title" id="${ids.title}">${isolated(input.title)}</h2>
        <p class="perspectify-consent-intro" id="${ids.intro}">${escapeHtml(text.preferencesIntro)}</p>
      </div>
      <div class="perspectify-consent-body">
        <div class="perspectify-consent-choices">
          ${
            optional.length > 0
              ? `<div class="perspectify-consent-options perspectify-consent-options--${Math.min(optional.length, 4)}" role="group" aria-label="${escapeHtml(
                  text.optionalCategories
                )}">${optional.map((category) => renderCategoryOption(category, input)).join('')}</div>`
              : ''
          }
          <div class="perspectify-consent-required-row">
            ${
              required.length > 0
                ? `<p class="perspectify-consent-required">${LOCK_ICON}<span>${required
                    .map((category) => `${isolated(category.label)}: ${escapeHtml(text.alwaysOn)}`)
                    .join(' · ')}</span></p>`
                : '<span></span>'
            }
            <button type="button" class="perspectify-consent-disclosure perspectify-consent-about-toggle" data-disclosure="${ABOUT_DISCLOSURE_KEY}" aria-expanded="${aboutOpen}" aria-controls="${ids.about}"><span>${escapeHtml(
              text.aboutCategories
            )}</span>${CHEVRON_ICON}</button>
          </div>
          <dl class="perspectify-consent-about" id="${ids.about}"${aboutOpen ? '' : ' hidden'}>
            ${input.categories.map((category) => renderCategoryExplanation(category, input)).join('')}
          </dl>
        </div>
        <div class="perspectify-consent-toolbar">
          <h3 class="perspectify-consent-providers-title" id="${ids.providersTitle}">${escapeHtml(text.providers)}<span class="perspectify-consent-total">${
            input.providerTotal
          }</span></h3>
          ${
            hasProviders
              ? `<label class="perspectify-consent-search">${SEARCH_ICON}<input type="search" data-role="provider-search" placeholder="${escapeHtml(
                  text.searchPlaceholder
                )}" aria-label="${escapeHtml(text.searchLabel)}" aria-controls="${ids.list}" autocomplete="off" spellcheck="false" enterkeyhint="search"></label>`
              : ''
          }
        </div>
        <div class="perspectify-consent-provider-list" id="${ids.list}" role="region" aria-labelledby="${ids.providersTitle}"></div>
        ${
          hasProviders
            ? `<nav class="perspectify-consent-pager" aria-label="${escapeHtml(text.providerPages)}">
                <span class="perspectify-consent-range" data-role="provider-range"></span>
                <span class="perspectify-consent-pager-buttons">
                  <button type="button" data-action="previous-page" class="perspectify-consent-icon-button" aria-label="${escapeHtml(
                    text.previousPage
                  )}" title="${escapeHtml(text.previousPage)}">${ARROW_PREVIOUS_ICON}</button>
                  <button type="button" data-action="next-page" class="perspectify-consent-icon-button" aria-label="${escapeHtml(
                    text.nextPage
                  )}" title="${escapeHtml(text.nextPage)}">${ARROW_NEXT_ICON}</button>
                </span>
              </nav>`
            : ''
        }
        <p class="perspectify-consent-visually-hidden" id="${ids.status}" role="status" aria-live="polite" data-role="provider-status"></p>
        ${
          hasLegal
            ? `<div class="perspectify-consent-legal" id="${ids.legal}"${legalOpen ? '' : ' hidden'}>
                <p class="perspectify-consent-legal-heading">${escapeHtml(text.controllerDetails)}</p>
                ${input.legalDetails.map((line) => `<p class="perspectify-consent-legal-line">${isolated(line)}</p>`).join('')}
              </div>`
            : ''
        }
      </div>
      <div class="perspectify-consent-footer">
        <div class="perspectify-consent-actions">
          <button type="button" data-action="reject" class="perspectify-consent-button perspectify-consent-button--outline">${escapeHtml(text.rejectAll)}</button>
          <button type="button" data-action="accept" class="perspectify-consent-button perspectify-consent-button--outline">${escapeHtml(text.acceptAll)}</button>
          <button type="button" data-action="save" class="perspectify-consent-button perspectify-consent-button--accent perspectify-consent-save">${escapeHtml(text.saveChoices)}</button>
        </div>
        ${meta.length > 0 ? `<div class="perspectify-consent-meta">${meta.join('')}</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * One page of the provider list. A provider appears once, with every purpose it disclosed and
 * the category of each. The row only shows or hides that disclosure: it is not a consent control.
 */
export function renderProviderEntries(input: ConsentProviderEntriesInput): string {
  const { text } = input;
  const ids = preferencesIds(input.idPrefix);
  if (input.emptyKind === 'none') {
    return `<p class="perspectify-consent-empty">${escapeHtml(text.noProviders)}</p>`;
  }
  if (input.emptyKind === 'no-match') {
    return `<div class="perspectify-consent-empty"><p>${escapeHtml(text.noMatches)}</p><button type="button" data-action="clear-search" class="perspectify-consent-text-button">${escapeHtml(
      text.clearSearch
    )}</button></div>`;
  }
  return input.items
    .map((provider) => {
      const open = provider.id === input.openId;
      return `
        <div class="perspectify-consent-provider" data-provider-id="${escapeHtml(provider.id)}">
          <button type="button" class="perspectify-consent-provider-row" data-provider-row="${escapeHtml(provider.id)}" aria-expanded="${open}" aria-controls="${ids.detail(
            provider.id
          )}">
            <span class="perspectify-consent-provider-name">${isolated(provider.name)}</span>
            <span class="perspectify-consent-provider-summary" id="${ids.summary(provider.id)}">${isolated(provider.summary)}</span>
            <span class="perspectify-consent-details-label">${escapeHtml(text.details)}${CHEVRON_NEXT_ICON}</span>
          </button>
          <div class="perspectify-consent-provider-detail" id="${ids.detail(provider.id)}"${open ? '' : ' hidden'}>
            ${provider.purposes
              .map(
                (purpose) => `
                  <div class="perspectify-consent-purpose" data-category="${escapeHtml(purpose.category)}">
                    <p class="perspectify-consent-purpose-category">${isolated(purpose.categoryLabel)}</p>
                    <p class="perspectify-consent-purpose-text">${isolated(purpose.text)}</p>
                  </div>
                `
              )
              .join('')}
            ${
              provider.privacyPolicyUrl
                ? `<p class="perspectify-consent-provider-link-row">${externalLink(
                    provider.privacyPolicyUrl,
                    text.privacyPolicy,
                    text.providerPrivacyPolicy(provider.name),
                    'perspectify-consent-link'
                  )}</p>`
                : ''
            }
          </div>
        </div>
      `;
    })
    .join('');
}

const R = '.perspectify-consent-root';

/**
 * All selectors are namespaced under `.perspectify-consent-root`, and descendants are reset with
 * class-qualified selectors so ordinary host element styles (`p`, `button`, `h2`, `ul`) do not
 * reach in. Hosts can brand the UI through the `--perspectify-consent-*` custom properties.
 */
export const CONSENT_STYLES = `
${R} { all: initial; }
${R} {
  --pc-surface: var(--perspectify-consent-surface, #ffffff);
  --pc-text: var(--perspectify-consent-text, #0f172a);
  --pc-muted: var(--perspectify-consent-muted, #475569);
  --pc-divider: var(--perspectify-consent-divider, #dbe3ec);
  --pc-accent: var(--perspectify-consent-accent, #0369a1);
  --pc-accent-hover: #075985;
  --pc-on-accent: #ffffff;
  --pc-soft: #e0f2fe;
  --pc-well: #f8fafc;
  --pc-header-bg: var(--perspectify-consent-header-background, #07131d);
  --pc-header-text: #f1f5f9;
  --pc-header-muted: #cbd5e1;
  --pc-header-brand: #67e8f9;
  --pc-header-tile: #13283b;
  --pc-cat-functional: #2563eb;
  --pc-cat-analytics: #0e7490;
  --pc-cat-marketing: #c2410c;
  --pc-cat-support: #6d28d9;
  --pc-control-border: #94a3b8;
  --pc-hover: rgba(15, 23, 42, 0.06);
  --pc-filled-hover: #1e293b;
  --pc-backdrop: rgba(7, 19, 29, 0.55);
  --pc-shadow: 0 16px 48px rgba(15, 23, 42, 0.22);
  color-scheme: light;
  box-sizing: border-box;
  font-family: var(--perspectify-consent-font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--pc-text);
  letter-spacing: 0;
  text-align: start;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
}
@media (prefers-color-scheme: dark) {
  ${R} {
    --pc-surface: var(--perspectify-consent-surface, #0f172a);
    --pc-text: var(--perspectify-consent-text, #f1f5f9);
    --pc-muted: var(--perspectify-consent-muted, #94a3b8);
    --pc-divider: var(--perspectify-consent-divider, #334155);
    --pc-accent: var(--perspectify-consent-accent, #38bdf8);
    --pc-accent-hover: #7dd3fc;
    --pc-on-accent: #082f49;
    --pc-soft: #0c2a43;
    --pc-well: #162133;
    --pc-header-bg: var(--perspectify-consent-header-background, #07131d);
    --pc-header-text: #f1f5f9;
    --pc-header-muted: #cbd5e1;
    --pc-header-brand: #67e8f9;
    --pc-header-tile: #13283b;
    --pc-cat-functional: #93c5fd;
    --pc-cat-analytics: #67e8f9;
    --pc-cat-marketing: #fdba74;
    --pc-cat-support: #c4b5fd;
    --pc-control-border: #64748b;
    --pc-hover: rgba(241, 245, 249, 0.08);
    --pc-filled-hover: #e2e8f0;
    --pc-backdrop: rgba(0, 0, 0, 0.6);
    --pc-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
    color-scheme: dark;
  }
}
${R}[hidden], ${R} [hidden] { display: none !important; }
${R} *, ${R} *::before, ${R} *::after { box-sizing: border-box; letter-spacing: 0; }
${R} h2, ${R} h3, ${R} p, ${R} ul, ${R} li, ${R} dl, ${R} dt, ${R} dd {
  margin: 0; padding: 0; border: 0; background: none; font: inherit; color: inherit;
  text-transform: none; text-align: start; text-indent: 0; list-style: none;
}
${R} a { font: inherit; color: inherit; background: none; text-transform: none; }
${R} button {
  font: inherit; color: inherit; margin: 0; text-transform: none; text-shadow: none; box-shadow: none;
  -webkit-appearance: none; appearance: none; cursor: pointer; touch-action: manipulation; text-align: center;
}
${R} bdi { unicode-bidi: isolate; }
${R} .perspectify-consent-title, ${R} .perspectify-consent-banner-title,
${R} .perspectify-consent-button { -webkit-hyphens: auto; hyphens: auto; overflow-wrap: break-word; }
${R} .perspectify-consent-icon { display: block; flex: none; width: 16px; height: 16px; }

/* Buttons */
${R} .perspectify-consent-button {
  display: inline-flex; align-items: center; justify-content: center; min-height: 44px; min-width: 0;
  padding: 10px 16px; border: 1px solid var(--pc-control-border); border-radius: 6px;
  background: transparent; color: var(--pc-text); font-size: 14px; font-weight: 600; line-height: 1.25;
  white-space: normal; overflow-wrap: break-word;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}
${R} .perspectify-consent-button:hover { background: var(--pc-hover); }
${R} .perspectify-consent-button--filled { background: var(--pc-text); border-color: var(--pc-text); color: var(--pc-surface); }
${R} .perspectify-consent-button--filled:hover { background: var(--pc-filled-hover); border-color: var(--pc-filled-hover); }
${R} .perspectify-consent-button:focus-visible,
${R} .perspectify-consent-icon-button:focus-visible,
${R} .perspectify-consent-disclosure:focus-visible,
${R} .perspectify-consent-search input:focus-visible,
${R} .perspectify-consent-link:focus-visible { outline: 2px solid var(--pc-accent); outline-offset: 2px; }
${R} .perspectify-consent-link {
  display: inline-flex; align-items: center; gap: 4px; min-height: 44px; color: var(--pc-text);
  text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 3px; border-radius: 2px; cursor: pointer;
}
${R} .perspectify-consent-link .perspectify-consent-icon { width: 13px; height: 13px; }
${R} .perspectify-consent-link:hover { text-decoration-thickness: 2px; }

/* First-layer banner: a labelled, non-modal region. */
${R}.perspectify-consent-banner {
  position: fixed; z-index: 2147483600; display: block;
  left: 16px; right: 16px; bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  max-width: 1010px; max-height: calc(100vh - 32px); margin: 0 auto; overflow-y: auto; overscroll-behavior: contain;
  padding: 20px 24px; background: var(--pc-surface); border: 1px solid var(--pc-divider); border-radius: 8px;
  box-shadow: var(--pc-shadow); overflow-wrap: anywhere;
}
${R} .perspectify-consent-banner-inner { display: flex; flex-wrap: wrap; align-items: center; gap: 16px 32px; max-width: 960px; margin: 0 auto; }
${R} .perspectify-consent-banner-copy { flex: 1 1 360px; min-width: 0; }
${R} .perspectify-consent-banner-title { font-size: 16px; font-weight: 700; line-height: 1.35; color: var(--pc-text); }
${R} .perspectify-consent-copy { margin-top: 4px; color: var(--pc-muted); font-size: 14px; line-height: 1.5; }
${R} .perspectify-consent-link-row { margin-top: 6px; font-size: 13px; }
${R} .perspectify-consent-link-row .perspectify-consent-link, ${R} .perspectify-consent-provider-link-row .perspectify-consent-link { margin-block: -12px; }
${R} .perspectify-consent-banner-actions { display: flex; flex-wrap: wrap; gap: 8px; flex: 0 1 auto; min-width: 0; }
${R} .perspectify-consent-banner-actions .perspectify-consent-button { flex: 1 1 auto; min-width: 128px; }

/* Floating launcher: a 44px shield icon with the configured label as name and tooltip. */
${R}.perspectify-consent-manage {
  position: fixed; z-index: 2147483599; display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; padding: 0; border: 1px solid var(--pc-divider); border-radius: 8px;
  background: var(--pc-surface); color: var(--pc-text); box-shadow: 0 4px 14px rgba(15, 23, 42, 0.16);
  cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent;
  transition: background-color 140ms ease, color 140ms ease, border-color 140ms ease;
}
${R}.perspectify-consent-manage .perspectify-consent-icon { width: 20px; height: 20px; }
${R}.perspectify-consent-manage:hover { background-image: linear-gradient(var(--pc-hover), var(--pc-hover)); border-color: var(--pc-control-border); }
${R}.perspectify-consent-manage:focus-visible { outline: 2px solid var(--pc-accent); outline-offset: 2px; }
${R}.perspectify-consent-manage:active { background-image: linear-gradient(var(--pc-hover), var(--pc-hover)); color: var(--pc-accent); }
${R}.perspectify-consent-manage--bottom-right { right: 20px; bottom: calc(20px + env(safe-area-inset-bottom, 0px)); left: auto; top: auto; transform: none; }
${R}.perspectify-consent-manage--bottom-left { left: 20px; bottom: calc(20px + env(safe-area-inset-bottom, 0px)); right: auto; top: auto; transform: none; }
${R}.perspectify-consent-manage--top-right { right: 20px; top: calc(20px + env(safe-area-inset-top, 0px)); left: auto; bottom: auto; transform: none; }
${R}.perspectify-consent-manage--top-left { left: 20px; top: calc(20px + env(safe-area-inset-top, 0px)); right: auto; bottom: auto; transform: none; }
${R}.perspectify-consent-manage--middle-right { right: 20px; top: 50%; left: auto; bottom: auto; transform: translateY(-50%); }
${R}.perspectify-consent-manage--middle-left { left: 20px; top: 50%; right: auto; bottom: auto; transform: translateY(-50%); }
${R}.perspectify-consent-manage--bottom-right, ${R}.perspectify-consent-manage--top-right, ${R}.perspectify-consent-manage--middle-right { margin-right: env(safe-area-inset-right, 0px); }
${R}.perspectify-consent-manage--bottom-left, ${R}.perspectify-consent-manage--top-left, ${R}.perspectify-consent-manage--middle-left { margin-left: env(safe-area-inset-left, 0px); }
/* Sharing a corner with the feedback trigger: step clear of it rather than overlap. */
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--bottom-right,
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--bottom-left { bottom: calc(76px + env(safe-area-inset-bottom, 0px)); }
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--top-right,
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--top-left { top: calc(76px + env(safe-area-inset-top, 0px)); }
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--middle-right,
${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--middle-left { transform: translateY(calc(-50% - 104px)); }
${R} .perspectify-consent-tooltip {
  position: absolute; top: 50%; z-index: 1; width: max-content; max-width: 240px; padding: 6px 10px;
  border-radius: 6px; background: var(--pc-text); color: var(--pc-surface); box-shadow: 0 4px 14px rgba(15, 23, 42, 0.2);
  font-size: 13px; font-weight: 500; line-height: 1.35; text-align: start; white-space: normal; overflow-wrap: break-word;
  cursor: default; opacity: 0; visibility: hidden; transform: translateY(-50%);
  transition: opacity 120ms ease, visibility 0s linear 120ms;
}
${R}.perspectify-consent-manage--bottom-left .perspectify-consent-tooltip,
${R}.perspectify-consent-manage--top-left .perspectify-consent-tooltip,
${R}.perspectify-consent-manage--middle-left .perspectify-consent-tooltip { left: calc(100% + 8px); }
${R}.perspectify-consent-manage--bottom-right .perspectify-consent-tooltip,
${R}.perspectify-consent-manage--top-right .perspectify-consent-tooltip,
${R}.perspectify-consent-manage--middle-right .perspectify-consent-tooltip { right: calc(100% + 8px); }
/* The tooltip is part of the launcher, so hovering it keeps it open (WCAG 1.4.13 hoverable);
   this strip covers the 8px gap so the pointer can travel from the icon to the text. */
${R} .perspectify-consent-tooltip::before { content: ""; position: absolute; top: -6px; bottom: -6px; width: 10px; }
${R}.perspectify-consent-manage--bottom-left .perspectify-consent-tooltip::before,
${R}.perspectify-consent-manage--top-left .perspectify-consent-tooltip::before,
${R}.perspectify-consent-manage--middle-left .perspectify-consent-tooltip::before { right: 100%; }
${R}.perspectify-consent-manage--bottom-right .perspectify-consent-tooltip::before,
${R}.perspectify-consent-manage--top-right .perspectify-consent-tooltip::before,
${R}.perspectify-consent-manage--middle-right .perspectify-consent-tooltip::before { left: 100%; }
@media (hover: hover) {
  ${R}.perspectify-consent-manage:hover:not([data-tooltip-dismissed]) .perspectify-consent-tooltip { opacity: 1; visibility: visible; transition-delay: 0s; }
}
${R}.perspectify-consent-manage:focus-visible:not([data-tooltip-dismissed]) .perspectify-consent-tooltip { opacity: 1; visibility: visible; transition-delay: 0s; }

/* Preference dialog ("refined sheet"): dark header, category choices, a paged provider list
   that scrolls on its own, and a footer that always stays in view. */
${R}.perspectify-consent-overlay {
  position: fixed; inset: 0; z-index: 2147483601; display: flex; align-items: center; justify-content: center;
  padding: max(16px, env(safe-area-inset-top, 0px)) max(16px, env(safe-area-inset-right, 0px)) max(16px, env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px));
  background: var(--pc-backdrop); animation: perspectify-consent-fade 140ms ease-out;
  /* Sized by the viewport (or the visual viewport while a mobile keyboard is up), so the
     dialog can adapt to the height it actually has, not only to the window. */
  container: perspectify-consent-viewport / size;
}
@keyframes perspectify-consent-fade { from { opacity: 0; } to { opacity: 1; } }
${R} .perspectify-consent-modal {
  display: flex; flex-direction: column; width: min(680px, 100%); max-height: min(780px, 100%); min-height: 0;
  overflow: hidden; background: var(--pc-surface); color: var(--pc-text); border: 1px solid var(--pc-divider);
  border-top: 3px solid var(--pc-accent); border-radius: 8px; box-shadow: var(--pc-shadow); overflow-wrap: anywhere; outline: none;
}
${R} .perspectify-consent-visually-hidden {
  position: absolute !important; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden;
  clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; border: 0;
}
${R} .perspectify-consent-header {
  position: relative; flex: none; padding: 22px 26px 18px; background: var(--pc-header-bg); color: var(--pc-header-text);
}
${R} .perspectify-consent-brand { display: flex; align-items: center; gap: 8px; margin-bottom: 11px; padding-inline-end: 44px; color: var(--pc-header-brand); font-size: 12px; font-weight: 650; }
${R} .perspectify-consent-brand-mark { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 6px; background: var(--pc-header-tile); }
${R} .perspectify-consent-brand-mark .perspectify-consent-icon { width: 17px; height: 17px; }
${R} .perspectify-consent-header .perspectify-consent-close {
  position: absolute; top: 14px; inset-inline-end: 14px; width: 44px; height: 44px; margin: 0;
  border-radius: 5px; color: var(--pc-header-text);
}
${R} .perspectify-consent-header .perspectify-consent-close:hover { background: var(--pc-header-tile); color: var(--pc-header-text); }
${R} .perspectify-consent-header .perspectify-consent-close:focus-visible { outline-color: var(--pc-header-brand); }
${R} .perspectify-consent-title { padding-inline-end: 40px; font-size: 24px; font-weight: 650; line-height: 1.25; color: var(--pc-header-text); }
${R} .perspectify-consent-intro { margin-top: 7px; color: var(--pc-header-muted); font-size: 14px; }
${R} .perspectify-consent-icon-button {
  display: inline-flex; align-items: center; justify-content: center; flex: none; width: 44px; height: 44px;
  padding: 0; border: 0; border-radius: 5px; background: transparent; color: var(--pc-muted);
  transition: background-color 140ms ease, color 140ms ease;
}
${R} .perspectify-consent-icon-button:hover { background: var(--pc-soft); color: var(--pc-text); }
${R} .perspectify-consent-icon-button:disabled { opacity: 0.35; cursor: default; background: transparent; }
${R} .perspectify-consent-icon-button .perspectify-consent-icon { width: 17px; height: 17px; }
${R} .perspectify-consent-body { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }

/* Category choices */
${R} .perspectify-consent-choices { flex: none; padding: 8px 26px 12px; border-bottom: 1px solid var(--pc-divider); }
${R} .perspectify-consent-options { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0 14px; }
${R} .perspectify-consent-options--1 { grid-template-columns: minmax(0, 1fr); }
${R} .perspectify-consent-options--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
${R} .perspectify-consent-options--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
${R} .perspectify-consent-option {
  display: flex; align-items: center; gap: 7px; min-height: 46px; padding: 4px 0; border-bottom: 2px solid var(--pc-divider);
  font-size: 13px; font-weight: 600; line-height: 1.3; cursor: pointer; transition: border-color 140ms ease;
}
${R} .perspectify-consent-option:has(input:checked) { border-color: var(--pc-accent); }
${R} .perspectify-consent-option-label { min-width: 0; -webkit-hyphens: auto; hyphens: auto; overflow-wrap: break-word; }
${R} .perspectify-consent-option .perspectify-consent-icon { width: 17px; height: 17px; }
${R} .perspectify-consent-toggle {
  flex: none; width: 18px; height: 18px; margin: 0; margin-inline-start: auto; accent-color: var(--pc-accent); cursor: pointer;
  -webkit-appearance: auto; appearance: auto;
}
${R} .perspectify-consent-toggle:focus-visible { outline: 2px solid var(--pc-accent); outline-offset: 3px; }
${R} .perspectify-consent-option[data-category="functional"] .perspectify-consent-icon, ${R} .perspectify-consent-about-item[data-category="functional"] .perspectify-consent-icon { color: var(--pc-cat-functional); }
${R} .perspectify-consent-option[data-category="analytics"] .perspectify-consent-icon, ${R} .perspectify-consent-about-item[data-category="analytics"] .perspectify-consent-icon { color: var(--pc-cat-analytics); }
${R} .perspectify-consent-option[data-category="marketing"] .perspectify-consent-icon, ${R} .perspectify-consent-about-item[data-category="marketing"] .perspectify-consent-icon { color: var(--pc-cat-marketing); }
${R} .perspectify-consent-option[data-category="support"] .perspectify-consent-icon, ${R} .perspectify-consent-about-item[data-category="support"] .perspectify-consent-icon { color: var(--pc-cat-support); }
${R} .perspectify-consent-required-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0 12px; margin-top: 4px; }
${R} .perspectify-consent-required { display: flex; align-items: center; gap: 6px; min-width: 0; color: var(--pc-muted); font-size: 12px; }
${R} .perspectify-consent-required .perspectify-consent-icon { width: 13px; height: 13px; }
${R} .perspectify-consent-about { margin: 4px 0 6px; padding: 10px 14px; background: var(--pc-well); border-inline-start: 2px solid var(--pc-accent); font-size: 13px; }
${R} .perspectify-consent-about-item + .perspectify-consent-about-item { margin-top: 10px; }
${R} .perspectify-consent-about-term { display: flex; align-items: center; gap: 6px; margin: 0; font-weight: 650; }
${R} .perspectify-consent-about-term .perspectify-consent-icon { width: 15px; height: 15px; }
${R} .perspectify-consent-about-status { margin-inline-start: auto; color: var(--pc-muted); font-size: 12px; font-weight: 500; }
${R} .perspectify-consent-about-detail { margin: 2px 0 0; padding-inline-start: 21px; color: var(--pc-muted); }
${R} .perspectify-consent-service-list { margin-top: 6px; display: flex; flex-direction: column; gap: 4px; font-size: 12px; }
${R} .perspectify-consent-service-name { display: block; font-weight: 600; color: var(--pc-text); }
${R} .perspectify-consent-service-copy { display: block; }

/* Disclosures (category explanations, controller details) */
${R} .perspectify-consent-disclosure {
  display: inline-flex; align-items: center; gap: 6px; min-height: 44px; padding: 0; border: 0; border-radius: 4px;
  background: transparent; color: var(--pc-accent); font-size: 12px; font-weight: 600; line-height: 1.3; text-align: start;
}
${R} .perspectify-consent-disclosure:hover { text-decoration: underline; text-underline-offset: 3px; background: transparent; }
${R} .perspectify-consent-disclosure [data-icon="chevron"] { width: 14px; height: 14px; transition: transform 140ms ease; }
${R} .perspectify-consent-disclosure[aria-expanded="true"] [data-icon="chevron"] { transform: rotate(180deg); }

/* Provider toolbar, list and pager */
${R} .perspectify-consent-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex: none; padding: 10px 26px; }
${R} .perspectify-consent-providers-title { display: flex; align-items: baseline; gap: 6px; font-size: 15px; font-weight: 650; white-space: nowrap; }
${R} .perspectify-consent-total { color: var(--pc-muted); font-size: 12px; font-weight: 400; font-variant-numeric: tabular-nums; }
${R} .perspectify-consent-search {
  display: flex; align-items: center; gap: 7px; width: 200px; min-width: 0; min-height: 40px;
  border-bottom: 1px solid var(--pc-divider); color: var(--pc-muted); cursor: text;
}
${R} .perspectify-consent-search:focus-within { border-color: var(--pc-accent); }
${R} .perspectify-consent-search input {
  -webkit-appearance: none; appearance: none; width: 100%; min-width: 0; min-height: 38px; margin: 0; padding: 0;
  border: 0; border-radius: 0; background: transparent; color: var(--pc-text); font: inherit; font-size: 14px; outline: none;
}
${R} .perspectify-consent-search input::placeholder { color: var(--pc-muted); opacity: 1; }
${R} .perspectify-consent-provider-list {
  flex: 1 1 auto; min-height: 120px; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; padding: 0 26px;
  scrollbar-width: thin; scrollbar-color: var(--pc-divider) transparent;
}
${R} .perspectify-consent-provider { border-bottom: 1px solid var(--pc-divider); }
${R} .perspectify-consent-provider:last-child { border-bottom: 0; }
${R} .perspectify-consent-provider-row {
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr) auto; align-items: center; gap: 12px;
  width: 100%; min-height: 51px; padding: 10px 0; border: 0; border-radius: 0; background: transparent; color: var(--pc-text); text-align: start;
}
${R} .perspectify-consent-provider-row:hover .perspectify-consent-details-label { text-decoration: underline; text-underline-offset: 3px; }
${R} .perspectify-consent-provider-row:focus-visible { outline: 2px solid var(--pc-accent); outline-offset: 2px; }
${R} .perspectify-consent-provider-name { min-width: 0; font-size: 14px; font-weight: 650; line-height: 1.35; }
${R} .perspectify-consent-provider-summary { min-width: 0; color: var(--pc-muted); font-size: 13px; line-height: 1.4; }
${R} .perspectify-consent-details-label { display: inline-flex; align-items: center; justify-content: flex-end; gap: 3px; color: var(--pc-accent); font-size: 12px; font-weight: 600; white-space: nowrap; padding-inline-end: 3px; }
${R} .perspectify-consent-details-label .perspectify-consent-icon { width: 14px; height: 14px; transition: transform 140ms ease; }
${R} .perspectify-consent-provider-row[aria-expanded="true"] .perspectify-consent-provider-name { color: var(--pc-accent); }
${R} .perspectify-consent-provider-row[aria-expanded="true"] .perspectify-consent-details-label .perspectify-consent-icon { transform: rotate(90deg); }
${R} .perspectify-consent-provider-detail { margin: 0 0 10px; padding: 12px 16px 6px; background: var(--pc-well); border-inline-start: 2px solid var(--pc-accent); }
${R} .perspectify-consent-purpose { margin-bottom: 10px; font-size: 13px; line-height: 1.6; }
${R} .perspectify-consent-purpose-category { font-size: 12px; font-weight: 650; }
${R} .perspectify-consent-purpose-text { color: var(--pc-muted); }
${R} .perspectify-consent-provider-link-row { font-size: 12px; }
${R} .perspectify-consent-provider-link-row .perspectify-consent-link { margin-block: -8px; color: var(--pc-accent); }
${R} .perspectify-consent-empty { padding: 18px 0; color: var(--pc-muted); }
${R} .perspectify-consent-provider-list:has(> .perspectify-consent-empty) { min-height: 0; }
${R} .perspectify-consent-text-button {
  display: inline-flex; align-items: center; min-height: 44px; padding: 0; border: 0; background: transparent;
  color: var(--pc-accent); text-decoration: underline; text-underline-offset: 3px;
}
${R} .perspectify-consent-text-button:focus-visible { outline: 2px solid var(--pc-accent); outline-offset: 2px; }
${R} .perspectify-consent-pager {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex: none;
  padding: 2px 20px 2px 26px; border-top: 1px solid var(--pc-divider); color: var(--pc-muted); font-size: 12px; font-variant-numeric: tabular-nums;
}
${R} .perspectify-consent-pager-buttons { display: flex; gap: 4px; }
${R}[dir="rtl"] [data-icon="direction"], [dir="rtl"] ${R} [data-icon="direction"],
${R}[dir="rtl"] [data-icon="details-chevron"], [dir="rtl"] ${R} [data-icon="details-chevron"] { transform: scaleX(-1); }
${R}[dir="rtl"] .perspectify-consent-provider-row[aria-expanded="true"] [data-icon="details-chevron"],
[dir="rtl"] ${R} .perspectify-consent-provider-row[aria-expanded="true"] [data-icon="details-chevron"] { transform: rotate(90deg); }
${R}[dir="rtl"] [data-icon="external"], [dir="rtl"] ${R} [data-icon="external"] { transform: scaleX(-1); }

/* Controller details, inside the scrolling body. */
${R} .perspectify-consent-legal { flex: none; margin: 0 26px; padding: 12px 0 16px; border-top: 1px solid var(--pc-divider); font-size: 13px; }
${R} .perspectify-consent-legal-heading { font-weight: 700; color: var(--pc-text); }
${R} .perspectify-consent-legal-line { margin-top: 4px; color: var(--pc-muted); white-space: pre-line; }

/* Footer: the three decisions, always in view. */
${R} .perspectify-consent-footer { flex: none; padding: 14px 26px 8px; background: var(--pc-well); border-top: 1px solid var(--pc-divider); }
${R} .perspectify-consent-actions { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
${R} .perspectify-consent-actions .perspectify-consent-button { padding: 10px 8px; border-radius: 5px; font-size: 13px; font-weight: 650; line-height: 1.35; }
${R} .perspectify-consent-actions .perspectify-consent-button--outline { background: var(--pc-surface); border-color: var(--pc-divider); }
${R} .perspectify-consent-actions .perspectify-consent-button--outline:hover { background: var(--pc-soft); }
${R} .perspectify-consent-button--accent { background: var(--pc-accent); border-color: var(--pc-accent); color: var(--pc-on-accent); }
${R} .perspectify-consent-button--accent:hover { background: var(--pc-accent-hover); border-color: var(--pc-accent-hover); }
${R} .perspectify-consent-meta { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0 16px; font-size: 12px; }
${R} .perspectify-consent-meta-link { min-height: 44px; color: var(--pc-muted); }
${R} .perspectify-consent-legal-toggle { margin: 0; margin-inline-start: auto; color: var(--pc-muted); }

@media (max-width: 640px) {
  ${R}.perspectify-consent-banner { padding: 16px; }
  ${R} .perspectify-consent-banner-inner { flex-direction: column; align-items: stretch; gap: 12px; }
  ${R} .perspectify-consent-banner-copy { flex: none; }
  ${R} .perspectify-consent-banner-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  ${R} .perspectify-consent-banner-actions .perspectify-consent-button { min-width: 0; }
  ${R} .perspectify-consent-banner-manage { grid-column: 1 / -1; }
  ${R}.perspectify-consent-manage--bottom-right { right: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }
  ${R}.perspectify-consent-manage--bottom-left { left: 12px; bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }
  ${R}.perspectify-consent-manage--top-right { right: 12px; top: calc(12px + env(safe-area-inset-top, 0px)); }
  ${R}.perspectify-consent-manage--top-left { left: 12px; top: calc(12px + env(safe-area-inset-top, 0px)); }
  ${R}.perspectify-consent-manage--middle-right { right: 12px; }
  ${R}.perspectify-consent-manage--middle-left { left: 12px; }
  ${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--bottom-right,
  ${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--bottom-left { bottom: calc(68px + env(safe-area-inset-bottom, 0px)); }
  ${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--top-right,
  ${R}.perspectify-consent-manage--stacked.perspectify-consent-manage--top-left { top: calc(68px + env(safe-area-inset-top, 0px)); }
}
@media (max-width: 600px) {
  ${R} .perspectify-consent-header { padding: 18px 18px 14px; }
  ${R} .perspectify-consent-header .perspectify-consent-close { top: 10px; }
  ${R} .perspectify-consent-title { font-size: 20px; }
  ${R} .perspectify-consent-choices { padding: 2px 18px 10px; }
  ${R} .perspectify-consent-options { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 18px; }
  ${R} .perspectify-consent-options--1 { grid-template-columns: minmax(0, 1fr); }
  ${R} .perspectify-consent-toolbar { padding: 8px 18px; gap: 12px; }
  ${R} .perspectify-consent-search { width: 150px; min-height: 44px; }
  /* 16px keeps iOS from zooming the page when the field takes focus. */
  ${R} .perspectify-consent-search input { min-height: 42px; font-size: 16px; }
  ${R} .perspectify-consent-provider-list { padding: 0 18px; }
  ${R} .perspectify-consent-provider-row { grid-template-columns: minmax(0, 1fr) auto; gap: 2px 8px; min-height: 59px; }
  ${R} .perspectify-consent-provider-summary { grid-column: 1; grid-row: 2; }
  ${R} .perspectify-consent-details-label { grid-column: 2; grid-row: 1 / 3; }
  ${R} .perspectify-consent-purpose { font-size: 14px; }
  ${R} .perspectify-consent-pager { padding: 2px 12px 2px 18px; }
  ${R} .perspectify-consent-legal { margin: 0 18px; }
  ${R} .perspectify-consent-footer { padding: 12px 18px 6px; }
  ${R} .perspectify-consent-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  ${R} .perspectify-consent-save { grid-column: 1 / -1; }
}
@media (max-width: 359px) {
  ${R} .perspectify-consent-title { font-size: 18px; }
  ${R} .perspectify-consent-intro { font-size: 13px; }
  ${R} .perspectify-consent-options { column-gap: 12px; }
  ${R} .perspectify-consent-option { gap: 4px; }
  ${R} .perspectify-consent-search { width: 120px; }
  ${R} .perspectify-consent-button { padding-inline: 10px; }
}
/* Short screens (landscape phones, 200% zoom, a raised keyboard): the whole middle scrolls as
   one region instead of squeezing the provider list to nothing. Footer actions stay in view. */
@media (max-height: 600px) {
  ${R} .perspectify-consent-header { padding-top: 12px; padding-bottom: 10px; }
  ${R} .perspectify-consent-brand { margin-bottom: 4px; }
  ${R} .perspectify-consent-header .perspectify-consent-close { top: 6px; }
  ${R} .perspectify-consent-title { font-size: 18px; }
  ${R} .perspectify-consent-intro { margin-top: 2px; font-size: 13px; }
  ${R} .perspectify-consent-provider-list { flex: none; min-height: 0; overflow: visible; }
  ${R} .perspectify-consent-footer { padding-top: 8px; padding-bottom: 2px; }
}
/* Very little height (a landscape phone with its keyboard up, heavy zoom): pinning the header
   and footer would leave the choices and providers no room at all. The whole sheet scrolls as
   one region instead, so every control can be reached and the focused one scrolled into view. */
@container perspectify-consent-viewport (max-height: 320px) {
  ${R} .perspectify-consent-modal { overflow-y: auto; overscroll-behavior: contain; }
  ${R} .perspectify-consent-body { flex: none; min-height: auto; overflow: visible; }
  ${R} .perspectify-consent-provider-list { flex: none; min-height: 0; overflow: visible; }
}
@media (prefers-reduced-motion: reduce) {
  ${R}, ${R} *, ${R} *::before, ${R} *::after { animation: none !important; transition: none !important; }
}
@media (forced-colors: active) {
  ${R}.perspectify-consent-manage { border-color: ButtonText; }
  ${R} .perspectify-consent-tooltip { border: 1px solid CanvasText; }
  ${R} .perspectify-consent-header { border-bottom: 1px solid CanvasText; }
  ${R} .perspectify-consent-provider-row[aria-expanded="true"] { outline: 1px solid Highlight; }
}
`;
